import React, { useEffect, useMemo, useRef, useState } from "react";

function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

export default function VideoFramePicker({
    file,
    fps = 25,
    width = 640,
    height = 360,
    onSelectFrame,
}) {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    const [videoUrl, setVideoUrl] = useState(null);
    const [duration, setDuration] = useState(0);
    const [sliderTime, setSliderTime] = useState(0);
    const [ready, setReady] = useState(false);
    const [seeking, setSeeking] = useState(false);
    const [hasUserPickedFrame, setHasUserPickedFrame] = useState(false);

    const frameStep = useMemo(() => 1 / fps, [fps]);

    useEffect(() => {
        if (!file) {
            setVideoUrl(null);
            setDuration(0);
            setSliderTime(0);
            setReady(false);
            setSeeking(false);
            setHasUserPickedFrame(false);
            return;
        }

        const url = URL.createObjectURL(file);

        setVideoUrl(url);
        setDuration(0);
        setSliderTime(0);
        setReady(false);
        setSeeking(false);
        setHasUserPickedFrame(false);

        return () => URL.revokeObjectURL(url);
    }, [file]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const drawCurrentFrame = () => {
            const canvas = canvasRef.current;
            if (!canvas || !video.videoWidth || !video.videoHeight) return;

            const ctx = canvas.getContext("2d");
            if (!ctx) return;

            canvas.width = width;
            canvas.height = height;

            ctx.clearRect(0, 0, width, height);
            ctx.fillStyle = "#000";
            ctx.fillRect(0, 0, width, height);

            const videoAspect = video.videoWidth / video.videoHeight;
            const targetAspect = width / height;

            let drawWidth, drawHeight, offsetX, offsetY;

            if (videoAspect > targetAspect) {
                drawWidth = width;
                drawHeight = width / videoAspect;
                offsetX = 0;
                offsetY = (height - drawHeight) / 2;
            } else {
                drawHeight = height;
                drawWidth = height * videoAspect;
                offsetX = (width - drawWidth) / 2;
                offsetY = 0;
            }

            ctx.drawImage(video, offsetX, offsetY, drawWidth, drawHeight);
            setSliderTime(video.currentTime);

            if (hasUserPickedFrame && onSelectFrame) {
                onSelectFrame({
                    timeSeconds: video.currentTime,
                    frameIndex: Math.round(video.currentTime * fps),
                });
            }
        };

        const handleLoadedMetadata = () => setDuration(video.duration || 0);
        const handleLoadedData = () => {
            setReady(true);
            drawCurrentFrame();
        };
        const handleSeeked = () => {
            drawCurrentFrame();
            setSeeking(false);
        };

        video.addEventListener("loadedmetadata", handleLoadedMetadata);
        video.addEventListener("loadeddata", handleLoadedData);
        video.addEventListener("seeked", handleSeeked);

        return () => {
            video.removeEventListener("loadedmetadata", handleLoadedMetadata);
            video.removeEventListener("loadeddata", handleLoadedData);
            video.removeEventListener("seeked", handleSeeked);
        };
    }, [videoUrl, width, height, fps, hasUserPickedFrame, onSelectFrame]);

    const seekTo = (time) => {
        const video = videoRef.current;
        if (!video || !ready) return;

        const maxTime = Math.max(duration - frameStep, 0);
        setSeeking(true);
        video.currentTime = clamp(time, 0, maxTime);
    };

    const handleSliderChange = (e) => {
        setHasUserPickedFrame(true);
        setSliderTime(parseFloat(e.target.value));
    };

    const handleSliderCommit = () => seekTo(sliderTime);

    if (!file || !videoUrl) return null;

    return (
        <div style={{ marginTop: 24 }}>
            <div style={{ marginBottom: 8, fontWeight: 600 }}>
                Vælg preview-billede
            </div>

            <video
                ref={videoRef}
                src={videoUrl}
                preload="metadata"
                muted
                playsInline
                style={{ display: "none" }}
            />

            <canvas
                ref={canvasRef}
                style={{
                    width: "100%",
                    maxWidth: width,
                    border: "1px solid #d0d0d0",
                    borderRadius: 4,
                    display: "block",
                    background: "#111",
                }}
            />

            <div style={{ marginTop: 12 }}>
                <input
                    type="range"
                    min={0}
                    max={Math.max(duration, 0)}
                    step={frameStep}
                    value={sliderTime}
                    onChange={handleSliderChange}
                    onMouseUp={handleSliderCommit}
                    onTouchEnd={handleSliderCommit}
                    disabled={!ready || seeking}
                    style={{ width: "100%" }}
                />
            </div>
        </div>
    );
}